/* eslint-disable no-nested-ternary */
/* eslint-disable prefer-arrow-callback */
/* eslint-disable no-underscore-dangle */
import { Autocomplete, FormHelperText, Grid, Modal, Stack } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { Form, Formik } from "formik";
import moment from "moment/moment";
import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import Loader from "utils/Loader";
import * as Yup from "yup";
import MDInput from "components/MDInput";
import allTimezones from "lib/timezone_constants";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { competitionActivate, reRunCompetition, fetchCompetition } from "apis/compete";

const validationSchema = Yup.object({
  startDate: Yup.date()
    .required("Start Time is required")
    .typeError("Start Time is required")
    .test(
      "invalid start time",
      "Start Time must be 15 minutes greater than selected timezone's current time",
      function (value, ctx) {
        const timezone = ctx?.from?.[0].value?.timezone;
        if (
          moment.tz(value, timezone).isBefore(moment.tz(undefined, timezone).add(15, "minutes"))
        ) {
          return false;
        }
        return true;
      }
    ),
  endDate: Yup.date()
    .typeError()
    .test("required", "End Time is required", function (value, ctx) {
      if (ctx?.from?.[0]?.value?.winCondition !== "First To KPI Target" && !value) {
        return false;
      }
      return true;
    })
    .test(
      "invalid end time",
      "End Time must be 5 minutes greater than start time",
      function (value, ctx) {
        const timezone = ctx?.from?.[0].value?.timezone;

        if (
          value &&
          moment
            .tz(value, timezone)
            .isBefore(moment.tz(ctx?.from?.[0].value?.startDate, timezone).add(5, "minutes"))
        ) {
          return false;
        }

        return true;
      }
    ),
  timezone: Yup.string().required("Timezone is required"),
});

function RunCompetition({
  open,
  setOpen,
  competition,
  setCompetition,
  activateCompetition,
  setActivateCompetition,
  setPayload,
}) {
  const [initialValues, setInitialValues] = useState({
    startDate: "",
    endDate: "",
    timezone: null,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [httpError, setHttpError] = useState({ error: false, message: "" });
  const [competitionData, setCompetitionData] = useState();
  const [competitionLoading, setCompetitionLoading] = useState(false);

  const [controller] = useMaterialUIController();

  const handleClose = () => {
    if (!submitLoading) {
      setInitialValues({
        startDate: "",
        endDate: "",
        timezone: null,
      });
      setCompetition();
      setHttpError({ error: false, message: "" });
      setOpen(false);
      setActivateCompetition(false);
    }
  };

  const getCompetition = async () => {
    try {
      setCompetitionLoading(true);
      const response = await fetchCompetition(competition?.id);
      if (response?.data?.success) {
        setCompetitionData(response.data.data);
      } else if (response?.response?.status !== 403) {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      } else if (response?.response?.status === 403) {
        handleClose();
      }
      setCompetitionLoading(false);
    } catch (error) {
      setCompetitionLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    if (open) {
      setInitialValues({
        startDate: "",
        endDate: "",
        timezone: null,
      });
      getCompetition();
    }
    if (!open) {
      moment.tz.setDefault(moment.tz.guess());
    }
  }, [open]);

  useEffect(() => {
    if (competitionData) {
      setInitialValues((prevState) => ({
        ...prevState,
        timezone: competitionData?.timezone,
        winCondition: competitionData?.CompetitionWinCondition?.condition,
      }));
      moment.tz.setDefault(competitionData?.timezone);
    }
  }, [competitionData]);

  // eslint-disable-next-line consistent-return
  const generateHelperTextForStartDate = (value, errors, touched) => {
    if (value && touched?.startDate && errors?.startDate) {
      return errors?.startDate;
    }
    if (!value && touched?.startDate && errors?.startDate) {
      return "Start Time is required";
    }
    if (!value && touched?.endDate && errors?.endDate) {
      return "Start Time is required";
    }
  };

  // eslint-disable-next-line consistent-return
  const generateErrorForStartDate = (value, errors, touched) => {
    if (value && touched?.startDate && Boolean(errors?.startDate)) {
      return true;
    }
    if (!value && touched?.startDate && Boolean(errors?.startDate)) {
      return true;
    }
    if (!value && touched?.endDate && errors?.endDate) {
      return true;
    }
  };

  const { darkMode } = controller;

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: window.innerWidth < 700 ? "100%" : 850,
    maxHeight: window.innerHeight < 800 ? 600 : 700,
    bgcolor: darkMode ? "#1F283E" : "background.paper",
    overflow: "auto",
    boxShadow: 24,
    p: 4,
    outline: "none",
  };

  const handleSubmit = async (data) => {
    try {
      const payload = {
        competitionId: competition?.id,
        startDatetime: data?.startDate?.format("DD-MM-YYYY HH:mm"),
        endDatetime: data?.endDate?.format("DD-MM-YYYY HH:mm"),
        timezone: competition?.timezone,
      };
      let response;
      setSubmitLoading(true);
      if (activateCompetition) {
        response = await competitionActivate(payload);
      } else {
        response = await reRunCompetition(payload);
      }
      if (response?.data?.success) {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: response?.data?.message || "Competition successfully activated.",
          }),
          {
            variant: "custom",
          }
        );
        setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
      } else if (response?.response?.status !== 403) {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
        setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
      } else if (response?.response?.status === 403) {
        handleClose();
      }
      setSubmitLoading(false);
    } catch (error) {
      setSubmitLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.response.data.message || error.message || "Something went wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={style}>
        <MDBox sx={{ position: "relative" }}>
          <Loader loading={competitionLoading} />
          <MDTypography variant="h4" mb={2}>
            Choose a Start and End Time
          </MDTypography>

          <MDBox sx={{ position: "relative" }}>
            <Formik
              enableReinitialize
              validateOnChange
              validationSchema={validationSchema}
              initialValues={initialValues}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, setFieldValue }) => (
                <Form>
                  <Stack
                    spacing={2}
                    sx={{
                      "& .MuiFormHelperText-root": { color: "#d32f2f" },
                    }}
                  >
                    <Grid container spacing={2}>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Grid item xs={12} sm={12} md={6}>
                          <DateTimePicker
                            label="Start Time"
                            placeholder="Start Time"
                            disablePast
                            // ampm={false}
                            value={values?.startDate || null}
                            minDateTime={moment.tz(undefined, values.timezone).add(15, "minutes")}
                            onChange={(value) => {
                              setFieldValue("startDate", value || null);
                            }}
                            viewRenderers={{
                              hours: null,
                              minutes: null,
                              seconds: null,
                            }}
                            slotProps={{
                              textField: {
                                variant: "standard",
                                fullWidth: true,
                                error: generateErrorForStartDate(
                                  values?.startDate,
                                  errors,
                                  touched
                                ),
                                helperText: generateHelperTextForStartDate(
                                  values?.startDate,
                                  errors,
                                  touched
                                ),
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                          <DateTimePicker
                            label="End Time"
                            placeholder="End Time"
                            disablePast
                            // ampm={false}
                            value={values?.endDate || null}
                            minDateTime={
                              !values.startDate
                                ? moment.tz(undefined, values.timezone)
                                : moment.tz(values.startDate, values.timezone)?.add(5, "minutes")
                            }
                            onChange={(value) => {
                              setFieldValue("endDate", value || null);
                            }}
                            viewRenderers={{
                              hours: null,
                              minutes: null,
                              seconds: null,
                            }}
                            slotProps={{
                              textField: {
                                variant: "standard",
                                fullWidth: true,
                                error: touched?.endDate && Boolean(errors?.endDate),
                                helperText: touched?.endDate && errors?.endDate,
                              },
                            }}
                          />
                        </Grid>
                      </LocalizationProvider>
                      <Grid item xs={12} sm={12} md={6}>
                        <Autocomplete
                          fullWidth
                          disableClearable
                          disabled={activateCompetition}
                          getOptionLabel={(option) =>
                            `${option?.value || ""} ${
                              option?.title?.slice(0, option.title.indexOf(")") + 1) || ""
                            }`.trim()
                          }
                          value={allTimezones.find((_) => _.value === values.timezone) || null}
                          onChange={(_, selectedTimezone) => {
                            setFieldValue("timezone", selectedTimezone.value);
                            moment.tz.setDefault(selectedTimezone.value);
                            if (values.startDate) {
                              setFieldValue("startDate", null);
                            }
                            if (values.endDate) {
                              setFieldValue("endDate", null);
                            }
                          }}
                          options={allTimezones}
                          renderInput={(params) => (
                            <MDInput variant="standard" {...params} label="Select timezone" />
                          )}
                        />
                      </Grid>
                      {httpError.error && (
                        <Grid item xs={12} sm={12} md={12} lg={12}>
                          <MDBox
                            sx={{
                              "& .MuiFormHelperText-root": {
                                color: "#d32f2f",
                                fontSize: "16px",
                                mt: 2,
                              },
                            }}
                          >
                            {" "}
                            <FormHelperText sx={{ marginTop: "0px !important" }}>
                              {httpError.message}
                            </FormHelperText>
                          </MDBox>
                        </Grid>
                      )}
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={12}
                        display="flex"
                        justifyContent="flex-end"
                        mt={2}
                      >
                        <MDButton
                          variant="gradient"
                          color="secondary"
                          size="small"
                          onClick={handleClose}
                          sx={{ mt: 1, mr: 1, position: "relative" }}
                        >
                          Cancel
                        </MDButton>
                        <MDButton
                          variant="gradient"
                          color="newColor"
                          size="small"
                          type="submit"
                          disabled={submitLoading}
                          sx={{ mt: 1, position: "relative" }}
                        >
                          {submitLoading
                            ? "Loading..."
                            : activateCompetition
                            ? "Activate Competition"
                            : "Run Competition"}
                        </MDButton>
                      </Grid>
                    </Grid>
                  </Stack>
                </Form>
              )}
            </Formik>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

export default RunCompetition;
